import { useTranslation } from 'react-i18next'
import { NavigationDataT } from '@/lib/types/navigationTypes.ts'
import {
  Bell,
  ChevronLeft,
  CircleUser,
  CloudOff,
  Cog,
  Folders,
  GlobeIcon,
  HardDrive,
  House,
  HouseIcon,
  Inbox,
  ListTodo,
  RefreshCw,
  Scroll,
  ShieldCheck,
  Smartphone,
  TablePropertiesIcon,
  Tags,
  Users,
} from 'lucide-react'
import { useProjectStageStore } from '@/components/common/menu/lib/store/useProjectStageStore.tsx'
import getProjectStatusIcon from '@/lib/helpers/getProjectStatusIcon.tsx'
import { Reported } from '@/assets/icons/statusIcons.tsx'
import { Dot } from '@/assets/icons/dot.tsx'
export default function useNavData() {
  const { t } = useTranslation()

  const links = useProjectStageStore(state => state.projectStageIssuesLinks)
  const currProjectStageIri = useProjectStageStore(state => state.iri)
  const unreadNotificationsCount = useProjectStageStore(state => state.unreadNotificationsCount)
  const currProjectStage = useProjectStageStore(state => state.currProjectStage)
  const link = `/settings/organization${currProjectStage?.project?.['@id']}${currProjectStageIri}`

  const projectDetails = {
    name: t(`navigation.settings.projectDetails`),
    icon: getProjectStatusIcon(currProjectStage?.status).icon,
    link: link,
  }

  const primaryMenuData: NavigationDataT = {
    summary: {
      items: [
        {
          name: t(`navigation.project.summary`),
          icon: <HouseIcon className={`size-4`} />,
          link: `${currProjectStageIri}/dashboard`,
        },
        {
          name: t(`navigation.project.mail`),
          icon: <Inbox className={`size-4`} />,
          link: `${currProjectStageIri}/notifications`,
          notifications: unreadNotificationsCount,
        },
      ],
    },
    general: {
      items: [
        {
          name: t(`navigation.settings.global.issuesList`),
          icon: <TablePropertiesIcon className={`size-4`} />,
          link: `${currProjectStageIri}/archive?order=DESC&sort_by=CREATED_AT&view=ROWS`,
        },
        {
          name: t(`navigation.settings.global.reports`),
          icon: <Scroll className={`size-4`} />,
          link: `${currProjectStageIri}/reports`,
        },
      ],
    },
    issues: {
      items: links.map(link => {
        return {
          ...link,
          icon: <Dot className="size-4" fill={link.color} />,
        }
      }),
    },
    bgSync: {
      items: [
        {
          name: t(`navigation.project.syncQueue`),
          icon: <RefreshCw className={`size-4`} />,
          link: `${currProjectStageIri}/sync-queue`,
        },
      ],
    },

    // Logout used to live here as a second row beside "Ustawienia". It's
    // been promoted to an icon-only button in the footer next to the user
    // identity (see NavigationGroups primary footer) — that's where the
    // "log out" gesture belongs contextually, and it frees the nav list
    // from a destructive action sitting in the same vertical rhythm as
    // regular destinations. Ustawienia stays as the only primary-footer
    // nav item.
    settings: {
      pushToBottom: true,
      items: [
        {
          name: t(`navigation.project.settings`),
          icon: <Cog className="size-4" />,
          link: link,
        },
        // Super-admin console link — only rendered for special users (NavigationGroupsGroup
        // filters on specialUserOnly). Kept after the project-settings item so the
        // no-active-stage shift() below removes that item, not this one.
        {
          name: t('navigation.superuser'),
          icon: <ShieldCheck className="size-4" />,
          link: '/superuser',
          specialUserOnly: true,
        },
      ],
    },
  }

  const secondaryMenuData: NavigationDataT = {
    backToApp: {
      items: [
        {
          name: t('navigation.backToApp'),
          icon: <ChevronLeft className={`size-4`} />,
          function: 'backToApp',
        },
      ],
    },
    projectsSettings: {
      title: t('navigation.project.projectsSettings'),
      items: [
        projectDetails,
        {
          name: t(`navigation.settings.organization.projects`),
          icon: <Folders className={`size-4`} />,
          link: '/settings/organization/projects',
        },
      ],
    },
    userSettings: {
      title: t('navigation.settings.users.title'),
      items: [
        {
          name: t('navigation.settings.users.account'),
          icon: <CircleUser className="size-4" />,
          link: '/settings/account',
        },
        {
          name: t('navigation.settings.users.notifications'),
          icon: <Bell className="size-4" />,
          link: '/settings/notifications',
        },
        {
          name: t('settings.pushDevices.navLabel'),
          icon: <Smartphone className="size-4" />,
          link: '/settings/push-devices',
        },
        {
          name: t('offlineCache.navLabel'),
          icon: <HardDrive className="size-4" />,
          link: '/settings/offline-cache',
        },
        {
          allowedRoles: ['ROLE_ORGANIZATION_OWNER', 'ROLE_ORGANIZATION_ADMIN'],
          name: t('navigation.settings.organization.organization'),
          icon: <House className="size-4" />,
          link: '/settings/organization/information',
        },
        {
          name: t(`navigation.settings.users.language`),
          icon: <GlobeIcon className={`size-4`} />,
          function: 'changeLang',
        },
      ],
    },
    // Renamed from "Ustawienia globalne" → "Ustawienia organizacji". The
    // previous name implied app-level/system settings; the items are really
    // about scoping the organization (its team, issue types, labels, etc.),
    // so the org label reads more accurately. Team (Zespół) moved here from
    // "Ustawienia użytkownika" — managing the company's roster belongs with
    // other org-scoped config, not next to personal account/language prefs.
    global: {
      allowedRoles: ['ROLE_ORGANIZATION_OWNER', 'ROLE_ORGANIZATION_ADMIN'],
      title: t('navigation.settings.global.title'),
      items: [
        {
          name: t('navigation.settings.global.team'),
          icon: <Users className="size-4" />,
          link: '/settings/organization/users',
        },
        // Role i uprawnienia sits right under Zespół: both are people-
        // management surfaces (who's in the org → what they can do), so
        // grouping them adjacent reads as a single "people & access"
        // sub-cluster before the content-config items below.
        {
          name: t('navigation.settings.global.users'),
          icon: <Users className="h-4 w-4" />,
          link: '/settings/project-roles',
        },
        {
          name: t('navigation.settings.global.issues'),
          icon: <ListTodo className="h-4 w-4" />,
          link: '/settings/issue_types',
        },
        {
          name: t('navigation.settings.global.labels'),
          icon: <Tags className="h-4 w-4" />,
          link: '/settings/labels',
        },
        {
          name: t('navigation.settings.global.statuses'),
          icon: <Reported className="h-4 w-4" />,
          link: '/settings/statuses',
        },
      ],
    },
    // Tryb offline pulled out as its own section at the very bottom of the
    // settings drawer. It used to be tucked under "Ustawienia użytkownika"
    // as a weird sibling of "Język" — not a navigation target but a toggle,
    // which confused the grouping. As a standalone trailing section it
    // reads as a global app-mode switch rather than a personal preference.
    // The item's `name` is used by NavigationGroupsGroup to dispatch to the
    // custom <NavOfflineModeToggle/> renderer (same trick as changeLang).
    offlineMode: {
      items: [
        {
          name: t('offlineMode.settings.title'),
          icon: <CloudOff className="size-4" />,
          function: 'offlineModeToggle',
        },
      ],
    },
  }

  if (!currProjectStage) {
    primaryMenuData.settings.items.shift()
    secondaryMenuData.projectsSettings.items = secondaryMenuData.projectsSettings.items.filter(
      item => item !== projectDetails,
    )
    // delete primaryMenuData.summary
    // delete primaryMenuData.general
  }

  return { primaryMenuData, secondaryMenuData }
}
